// FIX: Add import for React to resolve namespace error.
import React, { useCallback, useRef, Dispatch, SetStateAction } from 'react';
import { GameState, ManagerHistoryEntry, MatchEvent, Result } from '../../domain/entities';
import { FixturesHelper } from '../../services/FixturesHelper';
import { runWeeklySim } from '../../services/simulation/weekly';
import { advanceToNextSeason, getPlayerTeam, checkForSacking } from '../../services/GameService';
import { simulateMatch } from '../../services/MatchSimulator';

type SetGameState = Dispatch<SetStateAction<GameState | { gameStarted: false; }>>;
type SetBoolean = Dispatch<SetStateAction<boolean>>;
type SetSackedInfo = Dispatch<SetStateAction<any | null>>;
type SetString = Dispatch<SetStateAction<string>>;
type SetSimulationProgress = Dispatch<SetStateAction<{ title: string; playerMatchEvents: MatchEvent[] | null; result: Result | null; isPlayerMatch?: boolean; }>>;

// FIX: Export SackedInfo interface to be used in SackedScreen.
export interface SackedInfo {
    reason: string;
    oldTeamName: string;
    history: ManagerHistoryEntry[];
    newTeamName: string;
    newTeamLeague: string;
    currentSeasonEntry: ManagerHistoryEntry;
}

interface UseGameSimulationProps {
    gameState: GameState | { gameStarted: false };
    setGameState: SetGameState;
    setIsSimulating: SetBoolean;
    setSimulationTitle: SetString;
    setSimulationProgress: SetSimulationProgress;
    setSackedInfo: SetSackedInfo;
    isSimulating: boolean;
}

export const useGameSimulation = ({ gameState, setGameState, setIsSimulating, setSimulationTitle, setSimulationProgress, setSackedInfo, isSimulating }: UseGameSimulationProps) => {
    const isSimulatingRef = useRef(isSimulating);
    isSimulatingRef.current = isSimulating;

    const pendingStateRef = useRef<GameState | null>(null);

    const playWeek = useCallback(async (isAiPunditEnabled = false) => {
        if (isSimulatingRef.current || !gameState.gameStarted) return;

        setSimulationTitle('Simulating Week...');
        setIsSimulating(true);
        setSimulationProgress({ title: 'Preparing Matches...', playerMatchEvents: null, result: null });
        await new Promise(resolve => setTimeout(resolve, 50));

        const tempState = JSON.parse(JSON.stringify(gameState));
        const simulatedWeek = gameState.currentWeek;
        const { newState: finalState, playerMatchEvents, playerFixture } = await runWeeklySim(tempState, isAiPunditEnabled);

        if (playerMatchEvents && playerFixture) {
            const title = `${playerFixture.competition} | ${playerFixture.home} vs ${playerFixture.away}`;

            // Check if this is a cup final that the player is NOT in
            const isCupFinal = playerFixture.competition && playerFixture.competition.toLowerCase().includes('final');
            const isPlayerInMatch = playerFixture.home === gameState.playerTeamName || playerFixture.away === gameState.playerTeamName;
            const isPlayerMatch = !isCupFinal || isPlayerInMatch;

            setSimulationProgress({ title, playerMatchEvents, result: playerFixture.result || null, isPlayerMatch });
        } else {
             setSimulationProgress({ title: `Simulating Week ${Math.floor(simulatedWeek)}`, playerMatchEvents: null, result: null });
        }

        (finalState as any).gameStarted = true;

        // Store the final state in a ref - it will be applied when the match actually starts playing
        // This prevents save-scumming by not persisting results until the match playback begins
        pendingStateRef.current = finalState;

    }, [gameState, setIsSimulating, setSimulationTitle, setSimulationProgress]);
    
    const advanceSeason = useCallback(() => {
        setGameState(prevState => {
            if (!prevState.gameStarted) return prevState;

            // Calculate maxWeek the same way App.tsx does to account for cup weeks pushing fixtures later
            const maxWeek = FixturesHelper.getMaxWeek(prevState.fixtures);

            if (maxWeek > 0 && prevState.currentWeek > maxWeek) {
                const playerTeam = getPlayerTeam(prevState);
                const league = prevState.leagues[prevState.playerLeagueName];
                const finalPosition = league.teams.findIndex(t => t.name === playerTeam.name) + 1;
                const seasonTransactions = playerTeam.finances.history.filter(h => h.season === prevState.season);
                const spent = seasonTransactions.filter(h => h.event.startsWith('Bought')).reduce((sum, h) => sum - h.amount, 0);
                const income = seasonTransactions.filter(h => h.event.startsWith('Sold')).reduce((sum, h) => sum + h.amount, 0);
                const currentSeasonEntry: ManagerHistoryEntry = {
                    season: prevState.season, teamName: playerTeam.name, leagueName: league.name, position: finalPosition,
                    played: playerTeam.played, wins: playerTeam.won, draws: playerTeam.drawn, losses: playerTeam.lost,
                    goalsFor: playerTeam.goalsFor, goalsAgainst: playerTeam.goalsAgainst, spent, income,
                    ...(prevState.challenge && { challenge: prevState.challenge }),
                };

                const sackCheckResult = checkForSacking(playerTeam, league, prevState.leagues, prevState.config);
                if (sackCheckResult.sacked) {
                    setSackedInfo({
                        reason: sackCheckResult.reason!,
                        oldTeamName: prevState.playerTeamName,
                        history: [...prevState.managerHistory, currentSeasonEntry],
                        newTeamName: sackCheckResult.newTeamName!,
                        newTeamLeague: sackCheckResult.newLeagueName!,
                        currentSeasonEntry: currentSeasonEntry,
                    });
                    return prevState; // Don't advance season yet, wait for user interaction
                } else {
                    const newState: GameState = JSON.parse(JSON.stringify(prevState));
                    (newState as any).gameStarted = true;
                    newState.managerHistory.push(currentSeasonEntry);
                    return advanceToNextSeason(newState);
                }
            }
            return prevState;
        });
    }, [setGameState, setSackedInfo]);

    const continueAfterSacking = useCallback((sackedInfo: SackedInfo) => {
        setGameState(prevState => {
            if (!prevState.gameStarted) return prevState;

            if (!sackedInfo) {
                return prevState;
            }

            const newState: GameState = JSON.parse(JSON.stringify(prevState));
            (newState as any).gameStarted = true;

            newState.managerHistory.push(sackedInfo.currentSeasonEntry);
            newState.playerTeamName = sackedInfo.newTeamName;
            newState.playerLeagueName = sackedInfo.newTeamLeague;

            const finalState = advanceToNextSeason(newState);
            setSackedInfo(null);
            return finalState;
        });
    }, [setGameState, setSackedInfo]);

    const simulateFullSeason = useCallback(async (isAiPunditEnabled = false) => {
        if (isSimulatingRef.current || !gameState.gameStarted) return;
    
        setSimulationTitle('Simulating Season...');
        setIsSimulating(true);
        setSimulationProgress({ title: 'Simulating Rest of Season...', playerMatchEvents: null, result: null });
        await new Promise(resolve => setTimeout(resolve, 50));
    
        let tempState: GameState = JSON.parse(JSON.stringify(gameState));
        (tempState as any).gameStarted = true;
        const leagueConfig = tempState.config.LEAGUE_CONFIG[tempState.playerLeagueName];
        if (leagueConfig) {
            const maxWeeks = (leagueConfig.teams - 1) * 2;
            while (tempState.currentWeek <= maxWeeks) {
                const { newState } = await runWeeklySim(tempState, isAiPunditEnabled);
                tempState = newState;
            }
        }
        setGameState(tempState);
        setIsSimulating(false);
    }, [gameState, setGameState, setIsSimulating, setSimulationTitle, setSimulationProgress]);

    const simulateFourWeeks = useCallback(async (isAiPunditEnabled = false) => {
        if (isSimulatingRef.current || !gameState.gameStarted) return;

        setSimulationTitle('Simulating 4 Weeks...');
        setIsSimulating(true);
        setSimulationProgress({ title: 'Simulating 4 Weeks...', playerMatchEvents: null, result: null });
        await new Promise(resolve => setTimeout(resolve, 50));

        let tempState: GameState = JSON.parse(JSON.stringify(gameState));
        (tempState as any).gameStarted = true;
        for (let i = 0; i < 4; i++) {
            const leagueConfig = tempState.config.LEAGUE_CONFIG[tempState.playerLeagueName];
            if (!leagueConfig) break;
            const maxWeeks = (leagueConfig.teams - 1) * 2;
            if (tempState.currentWeek > maxWeeks) break;
            
            const { newState } = await runWeeklySim(tempState, isAiPunditEnabled);
            tempState = newState;
        }
        
        setGameState(tempState);
        setIsSimulating(false);
    }, [gameState, setGameState, setIsSimulating, setSimulationTitle, setSimulationProgress]);

    const dismissSimulation = useCallback((shouldApplyState = true) => {
        setIsSimulating(false);
        // Apply pending state only if requested (true for eliminations, false when changing tactics)
        if (shouldApplyState && pendingStateRef.current) {
            setGameState(pendingStateRef.current);
        }
        pendingStateRef.current = null;
    }, [setIsSimulating, setGameState]);

    const simulateToCupFinal = useCallback(async () => {
        if (isSimulatingRef.current || !gameState.gameStarted) return;

        setSimulationTitle('Simulating to Cup Final...');
        setIsSimulating(true);
        setSimulationProgress({ title: 'Simulating cup matches to the final...', playerMatchEvents: null, result: null });
        await new Promise(resolve => setTimeout(resolve, 50));

        let tempState: GameState = JSON.parse(JSON.stringify(gameState));
        (tempState as any).gameStarted = true;

        const cupNames = tempState.config.CUP_NAMES;
        const cup = tempState.cups[cupNames[0]]; // First cup
        const playerTeam = getPlayerTeam(tempState);

        if (cup && playerTeam && cup.rounds && cup.rounds.length > 0) {
            // Find the final round (last round)
            const finalRound = cup.rounds[cup.rounds.length - 1];

            // Simulate weeks until we reach the final round
            // Check if there are any cup fixtures before the final that haven't been played
            let allCupFixtures = FixturesHelper.getAllCupFixtures(tempState.fixtures, cup.name);
            let hasPreFinalFixtures = allCupFixtures.some(f => f.week < finalRound.week && !f.result);

            while (hasPreFinalFixtures) {
                // Run the full weekly simulation
                const { newState } = await runWeeklySim(tempState, false);
                tempState = newState;

                // After simulation, ensure player's team won any cup matches this week
                const thisWeek = Math.floor(tempState.currentWeek - 1);
                allCupFixtures = FixturesHelper.getAllCupFixtures(tempState.fixtures, cup.name);
                for (const fixture of allCupFixtures.filter(f => f.week === thisWeek && f.result)) {
                    const isPlayerHome = fixture.home === playerTeam.name;
                    const isPlayerAway = fixture.away === playerTeam.name;

                    if (isPlayerHome || isPlayerAway) {
                        // Check if player's team won
                        const playerWon = (isPlayerHome && fixture.result.homeScore > fixture.result.awayScore) ||
                                        (isPlayerAway && fixture.result.awayScore > fixture.result.homeScore);

                        if (!playerWon) {
                            // Adjust the score to ensure player wins
                            if (isPlayerHome) {
                                fixture.result.homeScore = fixture.result.awayScore + 1;
                            } else {
                                fixture.result.awayScore = fixture.result.homeScore + 1;
                            }
                        }
                    }
                }
            }

            // Now prepare the final with the player's team
            // Get all final round fixtures that don't have results yet
            allCupFixtures = FixturesHelper.getAllCupFixtures(tempState.fixtures, cup.name);
            const finalFixtures = allCupFixtures.filter(f => f.week >= finalRound.week && !f.result);

            if (finalFixtures.length > 0) {
                // Determine the opponent
                const finalFixture = finalFixtures[0];
                let opponent: any = null;

                // Check if away team already exists and is not the player
                if (finalFixture.away && finalFixture.away !== playerTeam.name) {
                    opponent = Object.values(tempState.leagues)
                        .flatMap(l => l.teams)
                        .find(t => t.name === finalFixture.away);
                } else if (finalFixture.home && finalFixture.home !== playerTeam.name) {
                    opponent = Object.values(tempState.leagues)
                        .flatMap(l => l.teams)
                        .find(t => t.name === finalFixture.home);
                }

                // If no opponent found, pick the strongest team
                if (!opponent) {
                    const allTeams = Object.values(tempState.leagues).flatMap(l => l.teams);
                    opponent = allTeams
                        .filter(t => t.name !== playerTeam.name)
                        .sort((a, b) => b.squad.reduce((sum, p) => sum + p.skill, 0) - a.squad.reduce((sum, p) => sum + p.skill, 0))[0];
                }

                if (opponent) {
                    // Set the final fixture with player's team
                    finalFixture.home = playerTeam.name;
                    finalFixture.away = opponent.name;
                }
            }
        }

        setGameState(tempState);
        setIsSimulating(false);
    }, [gameState, setGameState, setIsSimulating, setSimulationTitle, setSimulationProgress]);

    const simulateToSeasonWin = useCallback(async () => {
        if (isSimulatingRef.current || !gameState.gameStarted) return;

        setSimulationTitle('Simulating to Season Win...');
        setIsSimulating(true);
        setSimulationProgress({ title: 'Simulating remaining matches with guaranteed wins...', playerMatchEvents: null, result: null });
        await new Promise(resolve => setTimeout(resolve, 50));

        let tempState: GameState = JSON.parse(JSON.stringify(gameState));
        (tempState as any).gameStarted = true;

        const playerTeam = getPlayerTeam(tempState);
        if (!playerTeam) {
            setIsSimulating(false);
            return;
        }

        const leagueConfig = tempState.config.LEAGUE_CONFIG[tempState.playerLeagueName];
        if (!leagueConfig) {
            setIsSimulating(false);
            return;
        }

        const maxWeeks = (leagueConfig.teams - 1) * 2;

        // Simulate remaining weeks until season ends
        while (tempState.currentWeek <= maxWeeks) {
            // Run the full weekly simulation
            const { newState } = await runWeeklySim(tempState, false);
            tempState = newState;

            // After the simulation, check if player's team has a match result this week
            // and ensure they won (if they played)
            const leagueFixtures = tempState.fixtures[tempState.playerLeagueName];
            if (leagueFixtures) {
                const thisWeekFixtures = leagueFixtures.filter(f => f.week === Math.floor(tempState.currentWeek - 1) && f.result);
                for (const fixture of thisWeekFixtures) {
                    const isPlayerHome = fixture.home === playerTeam.name;
                    const isPlayerAway = fixture.away === playerTeam.name;

                    if (isPlayerHome || isPlayerAway) {
                        // Check if player's team won
                        const playerWon = (isPlayerHome && fixture.result.homeScore > fixture.result.awayScore) ||
                                        (isPlayerAway && fixture.result.awayScore > fixture.result.homeScore);

                        if (!playerWon) {
                            // Adjust the score to ensure player wins
                            if (isPlayerHome) {
                                fixture.result.homeScore = fixture.result.awayScore + 1;
                            } else {
                                fixture.result.awayScore = fixture.result.homeScore + 1;
                            }
                        }
                    }
                }
            }
        }

        setGameState(tempState);
        setIsSimulating(false);
    }, [gameState, setGameState, setIsSimulating, setSimulationTitle, setSimulationProgress]);

    const commitPendingState = useCallback(() => {
        if (pendingStateRef.current) {
            setGameState(pendingStateRef.current);
            pendingStateRef.current = null;
        }
    }, [setGameState]);

    return {
        playWeek,
        advanceSeason,
        continueAfterSacking,
        simulateFullSeason,
        simulateFourWeeks,
        simulateToCupFinal,
        simulateToSeasonWin,
        dismissSimulation,
        commitPendingState,
    };
};
